import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Modal } from '@mui/material'
import { LuTrash2 } from 'react-icons/lu'
import Button from '../Button/Button'
import Input from '../Input/Input'
import { showSnackBar } from '../Snackbar/Snackbar'
import { deleteContact, fetchContactDeleteImpact } from '../../features/contactDelete/contactDelete'

const STEP_IMPACT = 'impact'
const STEP_CONFIRM = 'confirm'
const LOAD_FALLBACK = 'Could not check what deleting this client affects.'
const DELETE_FALLBACK = 'Could not delete the client.'

const normalizeName = (value) => (value || '').trim().replace(/\s+/g, ' ')
const count = (value) => (Number.isFinite(value) ? value : 0)
const messageOf = (error, fallback) => (typeof error === 'string' && error ? error : fallback)

const impactRows = (impact) => [
  `Active payment plans that will be switched off: ${count(impact.plans?.active)}`,
  `Scheduled payments that will stop: ${count(impact.payments?.scheduled)}`,
  `Payments in processing (cannot be stopped, they will settle): ${count(impact.payments?.processing)}`,
  `Processed payments (stay in accounting): ${count(impact.payments?.processed)}`,
  `Documents kept: ${count(impact.documents?.total)} (signed: ${count(impact.documents?.signed)})`,
  `Co-applicants (their links are kept): ${count(impact.coApplicants)}`,
]

const DeleteContactDialog = ({ open, userId, onClose, onDeleted }) => {
  const dispatch = useDispatch()
  const [step, setStep] = useState(STEP_IMPACT)
  const [impact, setImpact] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [typedName, setTypedName] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  useEffect(() => {
    if (!open) return undefined
    let active = true
    setStep(STEP_IMPACT)
    setImpact(null)
    setLoadError('')
    setTypedName('')
    setDeleteError('')
    setDeleting(false)
    dispatch(fetchContactDeleteImpact(userId))
      .unwrap()
      .then((data) => {
        if (!active) return
        if (data) setImpact(data)
        else setLoadError(LOAD_FALLBACK)
      })
      .catch((error) => {
        if (active) setLoadError(messageOf(error, LOAD_FALLBACK))
      })
    return () => {
      active = false
    }
  }, [dispatch, open, userId])

  const expected = normalizeName(impact?.confirmText)
  const canDelete = !deleting && expected.length > 0 && normalizeName(typedName) === expected
  const loading = !impact && !loadError
  const confirmWord = impact && impact.confirmText === impact.contactId ? 'id' : 'name'

  const handleClose = () => {
    setStep(STEP_IMPACT)
    setTypedName('')
    setDeleteError('')
    if (onClose) onClose()
  }

  const handleRequestClose = () => {
    if (deleting) return
    handleClose()
  }

  const handleBack = () => {
    setStep(STEP_IMPACT)
    setTypedName('')
    setDeleteError('')
  }

  const handleDelete = async () => {
    if (!canDelete) return
    setDeleting(true)
    setDeleteError('')
    try {
      await dispatch(deleteContact(userId)).unwrap()
    } catch (error) {
      setDeleteError(messageOf(error, DELETE_FALLBACK))
      setDeleting(false)
      return
    }
    setDeleting(false)
    showSnackBar('success', 'Client deleted')
    if (onDeleted) onDeleted(impact)
    handleClose()
  }

  const renderBody = () => {
    if (step === STEP_CONFIRM) {
      return (
        <div>
          <p>
            {`To confirm, type the client's ${confirmWord} exactly: `}
            <strong className="text-text-primary">{impact?.confirmText}</strong>
          </p>
          <Input
            parentClassName="mt-4"
            width="w-full"
            label={`Client ${confirmWord}`}
            placeholder={`Client ${confirmWord}`}
            value={typedName}
            onChange={(event) => setTypedName(event.target.value)}
            disabled={deleting}
            autoComplete="off"
            autoFocus
          />
          {deleteError && <p role="alert" className="mt-3 text-sm text-error-main">{deleteError}</p>}
        </div>
      )
    }
    if (loadError) return <p role="alert" className="text-error-main">{loadError}</p>
    if (loading) return <p>Loading…</p>
    return (
      <div>
        <ul className="list-disc pl-5 space-y-2 mb-4">
          {impactRows(impact).map((row) => <li key={row}>{row}</li>)}
        </ul>
        <p>The client loses portal access and gets no more reminders, workflow emails or SMS. A developer can restore the client.</p>
      </div>
    )
  }

  const renderActions = () => {
    if (step === STEP_CONFIRM) {
      return (
        <>
          <Button onClick={handleBack} type="outlined" size="md" width="w-max" disabled={deleting}>Back</Button>
          <Button onClick={handleClose} type="outlined" size="md" width="w-max" disabled={deleting}>Cancel</Button>
          <Button
            classes="bg-primary-error-600"
            hover="hover:bg-primary-error-500" size="md" width="w-max"
            disabled={!canDelete} onClick={handleDelete}>Delete client</Button>
        </>
      )
    }
    return (
      <>
        <Button onClick={handleClose} type="outlined" size="md" width="w-max">Cancel</Button>
        {!loadError && (
          <Button size="md" width="w-max" disabled={!impact} onClick={() => setStep(STEP_CONFIRM)}>Continue</Button>
        )}
      </>
    )
  }

  return (
    <Modal open={open} onClose={handleRequestClose}>
      <div className="flex items-center justify-center h-screen">
        <div
          role="dialog"
          aria-labelledby="delete-contact-dialog-title"
          className="bg-paper rounded-2xl p-6 flex flex-col justify-between min-w-[400px] max-w-[480px] max-h-content">
          <div className="mb-6 border-b-black flex items-center">
            <LuTrash2 size={20} className="text-error-main" />
            <h4 id="delete-contact-dialog-title" className="font-sans text-lg-18 ml-[18px] text-text-primary">Delete client</h4>
          </div>
          <div className="font-sans max-h-[503px] overflow-auto text-text-secondary">
            {renderBody()}
          </div>
          <div className="mt-6 flex justify-end space-x-4">
            {renderActions()}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default DeleteContactDialog
